import { z } from 'zod/v4';

export const BulkDeleteQuestionnaireRequestsParamsSchema = z.object({
  id: z.string().min(1),
});

export type BulkDeleteQuestionnaireRequestsParams = z.infer<
  typeof BulkDeleteQuestionnaireRequestsParamsSchema
>;

/**
 * Body of `DELETE /api/questionnaires/:id/requests`, ported from the legacy
 * `bulkDelete.body` JSON Schema.
 *
 * One deliberate divergence: `questionnaireId` moved out of the body and into the
 * **path**, matching the sibling create endpoint
 * (`questionnaire-request-create.ts`). The two now agree on where a request
 * collection lives, and the id can no longer disagree with the resource being
 * addressed.
 *
 * That move is what makes the scoping fix expressible. The legacy service checked
 * each id's `created_by_company_id` but never checked the request actually
 * belonged to the `questionnaireId` it was sent with, so any request the calling
 * company had created could be deleted by pairing it with an unrelated
 * questionnaire the company also owned. The service now scopes the lookup by both
 * columns. No caller notices: both frontends only ever send ids selected from the
 * questionnaire being viewed.
 *
 * Constraints otherwise map 1:1: `minItems: 1` → `.min(1)`, `items: {type:
 * 'string'}` → `z.string().min(1)`. Deliberately **uncapped**, as the legacy route
 * was — a `.max()` would newly reject payloads that work today.
 *
 * Duplicates are tolerated rather than rejected: the service de-duplicates before
 * comparing found rows against requested ids, so `["a","a"]` deletes `a` once and
 * succeeds. The legacy loop validated per id and so also accepted duplicates;
 * rejecting them here would be a new 404 for a payload that works today.
 */
export const BulkDeleteQuestionnaireRequestsBodySchema = z.object({
  requestIds: z.array(z.string().min(1)).min(1),
});

export type BulkDeleteQuestionnaireRequestsBody = z.infer<
  typeof BulkDeleteQuestionnaireRequestsBodySchema
>;
